import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactionIcon from '../icon/ReactionIcon';
import {
  REACTIONS,
  DEFAULT_REACTION,
  getReaction,
} from '../../utils/reactions';

/**
 * Like button with LinkedIn-style hover reaction picker.
 * - Click: toggle default like (or remove current reaction)
 * - Hover/long-press: pick like / love / support / clap / insightful / funny / sad
 * - compact: smaller control for replies (icon + count)
 */
export default function ReactionButton({
  userReaction = null,
  onReact,
  iconSize = 16,
  compact = false,
  count = null,
  onCountClick,
  showLabel = true,
  className = '',
  pill = false,
}) {
  const [open, setOpen] = useState(false);
  const [pickerStyle, setPickerStyle] = useState(null);
  const hideTimer = useRef(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);
  const isMobile =
    typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  const active = Boolean(userReaction);
  const reaction = getReaction(userReaction || DEFAULT_REACTION);
  const pickerIconSize = compact ? 18 : 22;
  const pickerBtnSize = compact ? 30 : 36;

  const clearHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const showPicker = () => {
    clearHide();
    setOpen(true);
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = setTimeout(() => setOpen(false), 220);
  };

  const updatePickerPosition = () => {
    const btn = buttonRef.current;
    if (!btn || typeof window === 'undefined') return;

    const rect = btn.getBoundingClientRect();
    const gap = 8;
    const approxWidth = compact ? 220 : 280;
    const approxHeight = pickerBtnSize + 20;

    let left = rect.left + rect.width / 2;
    // Keep picker inside the viewport horizontally
    const minCenter = approxWidth / 2 + 8;
    const maxCenter = window.innerWidth - approxWidth / 2 - 8;
    left = Math.max(minCenter, Math.min(maxCenter, left));

    const spaceAbove = rect.top;
    const placeBelow = spaceAbove < approxHeight + gap + 12;

    if (placeBelow) {
      setPickerStyle({
        position: 'fixed',
        top: rect.bottom + gap,
        left,
        transform: 'translateX(-50%)',
        zIndex: 4000,
      });
    } else {
      setPickerStyle({
        position: 'fixed',
        top: rect.top - gap,
        left,
        transform: 'translate(-50%, -100%)',
        zIndex: 4000,
      });
    }
  };

  useEffect(() => () => clearHide(), []);

  useLayoutEffect(() => {
    if (!open) {
      setPickerStyle(null);
      return undefined;
    }
    updatePickerPosition();
    const onReposition = () => updatePickerPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, compact, pickerBtnSize]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPicker = pickerRef.current && pickerRef.current.contains(e.target);
      if (!inRoot && !inPicker) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const handleMainClick = (e) => {
    e.stopPropagation();
    if (isMobile && !open) {
      showPicker();
      return;
    }
    if (active) {
      onReact?.(null);
    } else {
      onReact?.(DEFAULT_REACTION);
    }
    setOpen(false);
  };

  const pickReaction = (type, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (userReaction === type) {
      onReact?.(null);
    } else {
      onReact?.(type);
    }
    setOpen(false);
  };

  const picker = open && pickerStyle
    ? createPortal(
        <div
          ref={pickerRef}
          role="menu"
          aria-label="Choose reaction"
          onMouseEnter={showPicker}
          onMouseLeave={scheduleHide}
          style={{
            ...pickerStyle,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 8px',
            background: '#fff',
            borderRadius: 28,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            border: '1px solid #e4e6eb',
            whiteSpace: 'nowrap',
          }}
        >
          {REACTIONS.map((r) => (
            <button
              key={r.type}
              type="button"
              role="menuitem"
              title={r.label}
              aria-label={r.label}
              onClick={(e) => pickReaction(r.type, e)}
              style={{
                background: userReaction === r.type ? '#f0f2f5' : 'transparent',
                border: 'none',
                borderRadius: '50%',
                width: pickerBtnSize,
                height: pickerBtnSize,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.28) translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <ReactionIcon type={r.type} size={pickerIconSize} />
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        flex: compact || pill ? '0 0 auto' : 1,
        display: 'inline-flex',
        justifyContent: compact || pill ? 'flex-start' : 'center',
        alignItems: 'center',
      }}
      onMouseEnter={!isMobile ? showPicker : undefined}
      onMouseLeave={!isMobile ? scheduleHide : undefined}
    >
      {picker}

      <button
        ref={buttonRef}
        type="button"
        className={[pill ? 'rt-like-pill' : '', className].filter(Boolean).join(' ') || undefined}
        onClick={handleMainClick}
        onContextMenu={(e) => {
          if (isMobile) {
            e.preventDefault();
            showPicker();
          }
        }}
        style={{
          background: pill ? '#fff' : 'transparent',
          border: pill ? '1.5px solid #cfe0ee' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: compact || pill ? 6 : 6,
          padding: pill ? '4px 10px' : compact ? '4px 8px' : '6px 12px',
          borderRadius: pill || compact ? 999 : 4,
          cursor: 'pointer',
          flex: compact || pill ? '0 0 auto' : 1,
          justifyContent: 'center',
          color: active ? reaction.color : '#65676b',
          fontWeight: compact || pill ? 600 : 600,
          fontSize: compact || pill ? 13 : 15,
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          width: compact || pill ? 'auto' : '100%',
          minWidth: 0,
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = pill ? '#f4f9fc' : '#f2f2f2';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = pill ? '#fff' : 'transparent';
        }}
        aria-label={active ? reaction.label : 'Like'}
        title="Hover to choose a reaction"
      >
        <ReactionIcon
          type={active ? reaction.type : DEFAULT_REACTION}
          size={iconSize}
          color={active ? reaction.color : (compact || pill ? '#94a3b8' : '#65676b')}
        />
        {showLabel && !compact && (
          <span
            className="post-action-text"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // Slightly tighter for longer labels like "Insightful"
              fontSize: active && reaction.label.length > 6 ? 13 : undefined,
            }}
          >
            {active ? reaction.label : 'Like'}
          </span>
        )}
        {compact && showLabel && active && (
          <span className="post-action-text" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {reaction.label}
          </span>
        )}
        {count !== null && count !== undefined && (
          <span
            onClick={(e) => {
              if (!onCountClick) return;
              e.stopPropagation();
              onCountClick(e);
            }}
            title={onCountClick ? 'See who reacted' : undefined}
            style={{
              color: active ? reaction.color : '#64748b',
              cursor: onCountClick ? 'pointer' : 'default',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            {count}
          </span>
        )}
      </button>
    </div>
  );
}
